import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import Lenis from "lenis";
import SplitTextAnime from "./splitTextAnime.es6";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(ScrollToPlugin);

export default class ScrollFunc {
    constructor() {
        this.lenis = null;
        this.splitTextAnime = null;
        this.init();
    }

    init() {
        this.splitTextAnime = new SplitTextAnime();
        this.set();
        this.setApng();
        this.smoothScroll();
        this.initLenis();
        // this.watchModalScroll();
    }

    set() {
        const revealElm = document.querySelectorAll(".js-scrollreveal, .js-splittext");

        revealElm.forEach((el) => {
            // .js-splittext は親に .js-splittext-content があればそれをトリガー位置にする(無ければ自身)
            const trigger = el.closest(".js-splittext-content") || el;

            ScrollTrigger.create({
                trigger,
                start: el.dataset.start || "top 70%",
                once: true,
                onEnter: (self) => {
                    el.classList.add("-reveal");
                    if (el.classList.contains("js-splittext")) {
                        this.splitTextAnime?.play(el);
                    }
                },
                //markers: true,
            });
        });
    }

    // .js-revealapng = 再生したいAPNGを持つ要素。きっかけの要素に -reveal が付いたら再生する。
    // data-reveal-target : 再生のきっかけにする要素のセレクタ(未指定なら自身)。CSSアニメする別要素に合わせたい時に使用
    //                      → きっかけ要素側の -reveal 付与(= data-start)にそのまま追従する
    // data-anim-delay    : きっかけ(-reveal付与)からAPNG再生までの待ち時間(秒)。CSSアニメの尺と連動させたい時に使用
    setApng() {
        const revealApng = document.querySelectorAll(".js-revealapng");

        revealApng.forEach((el) => {
            const trigger = el.dataset.revealTarget ? document.querySelector(el.dataset.revealTarget) : el;
            if (!trigger) return;

            const delay = parseFloat(el.dataset.animDelay) || 0;

            const fire = () => {
                if (delay > 0) {
                    setTimeout(() => this.playApng(el), delay * 1000);
                } else {
                    this.playApng(el);
                }
            };

            // きっかけ要素に -reveal が付いた瞬間に発火。既に付いていれば即発火。
            if (trigger.classList.contains("-reveal")) {
                fire();
                return;
            }
            const observer = new MutationObserver(() => {
                if (!trigger.classList.contains("-reveal")) return;
                observer.disconnect();
                fire();
            });
            observer.observe(trigger, { attributes: true, attributeFilter: ["class"] });
        });
    }

    initLenis() {
        this.lenis = new Lenis({
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        });

        //gsapのtickerと同期
        gsap.ticker.add((time) => {
            this.lenis.raf(time * 1000);
        });

        //Lenis使用の際は、gsapのlagSmoothingを無効化
        gsap.ticker.lagSmoothing(0);

        //LenisでスクロールするたびにScrollTriggerを更新
        this.lenis.on("scroll", ScrollTrigger.update);
    }

    // スクロール禁止/許可(Lenisのstop/startでホイール・タッチを抑止し、
    // 付与される lenis-stopped クラス → overflow:hidden でネイティブも止まる)
    lockScroll() {
        this.lenis?.stop();
    }

    unlockScroll() {
        this.lenis?.start();
    }

    // モーダル開閉(body.-inmodal)に追従して背景スクロールをロック/解除
    // (Lenis稼働中はbodyのoverflow:hiddenだけでは止まらないため)
    watchModalScroll() {
        let wasOpen = document.body.classList.contains("-inmodal");
        const observer = new MutationObserver(() => {
            const isOpen = document.body.classList.contains("-inmodal");
            if (isOpen === wasOpen) return; // -inmodalの切替時だけ反応
            wasOpen = isOpen;
            isOpen ? this.lockScroll() : this.unlockScroll();
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    }

    // ページ先頭(0)へ即時移動(Lenisの内部位置も同期させる)
    scrollToTop() {
        const toTop = () => {
            if (this.lenis) {
                this.lenis.scrollTo(0, { immediate: true, force: true });
                this.lenis.resize();
            }
            window.scrollTo(0, 0);
        };

        toTop();
        // Lenisのrafループ/画像読み込みで位置が戻されるのを上書きするため数フレーム叩く
        requestAnimationFrame(toTop);
        requestAnimationFrame(() => requestAnimationFrame(toTop));
    }

    // 対象に -reveal を付け、APNG(data-src / data-srcset を退避した画像)を読み込んで頭から再生させる
    playApng(el) {
        el.classList.add("-reveal");
        el.querySelectorAll("source[data-srcset]").forEach((source) => {
            source.srcset = source.dataset.srcset;
            source.removeAttribute("data-srcset");
        });
        el.querySelectorAll("img[data-src]").forEach((img) => {
            img.src = img.dataset.src;
            img.removeAttribute("data-src");
        });
    }

    smoothScroll() {
        const triggers = document.querySelectorAll(".js-smoothscroll");

        triggers.forEach((el) => {
            el.addEventListener("click", (e) => {
                e.preventDefault();
                const hash = el.getAttribute("href");

                const offset = window.innerWidth <= 768 ? window.innerWidth * 0.3 : 200 * (window.innerWidth / 1920);
                gsap.to(window, { duration: 1, scrollTo: { y: hash, offsetY: offset }, ease: "circ.inOut" });
            });
        });
    }
}
